import React, { useState, useEffect } from "react";
import { useIntl } from "react-intl";
import { useDispatch, useSelector } from "react-redux";
import { editUnit, setUnitState, editUnitExternalId } from "actions/Units";
import TextInput from "components/TextInput";
import SelectInput from "components/SelectInput";
import ButtonContained from "components/ButtonContained";

const UnitSettingsDeviceModal = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  const normalizeRentalState = (stateValue = "") => {
    const normalized = String(stateValue).trim().toLowerCase();

    if (normalized === "checked out" || normalized === "checked_out" || normalized === "checkedout") {
      return "checkout";
    }

    return normalized;
  };
  
  const unit = useSelector(state => state.units.unitObject?.unit);
  const authUser = useSelector(state => state.auth.authUser);
  const site = useSelector(state => state.auth.site);
  const canSelectCheckout = normalizeRentalState(unit?.rentalState) === "checkout";
  const [localUnit, setLocalUnit] = useState(null);

  useEffect(() => {
    if (unit) {
      setLocalUnit({
        ...unit,
        rentalState: normalizeRentalState(unit.rentalState),
      });
    }
  }, [unit]);

  const handleInputChange = (field, value) => {
    if (field === "rentalState" && value === "checkout" && !canSelectCheckout) {
      return;
    }

    setLocalUnit((prevUnit) => ({
      ...prevUnit,
      [field]: value,
    }));
  };

  const isCheckoutTransitionBlocked = localUnit?.rentalState === "checkout" && !canSelectCheckout;

  return (
    <div className="min-h-[300px] px-10 pt-4 grid grid-cols-2 gap-[80px]">
      {authUser?.permissions?.includes("manage_locks") && <div className="flex flex-col gap-4">
        <p className="tw-paragraph-lg">{intl.formatMessage({ id: "pages.units.edit" })}</p>
        <TextInput
          id="Unit-Settings-Device-Name-Input"
          dataCy="Unit-Settings-Device-Name-Input"
          placeholder={intl.formatMessage({id: "form.name"})}
          onChange={(e) => handleInputChange("name", e.target.value)}
          value={localUnit?.name ?? ""}
        />
        <ButtonContained
          id="Unit-Settings-Device-Save-Name"
          dataCy="Unit-Settings-Device-Save-Name"
          text={`${intl.formatMessage({ id: "comp.button.saveChanges" })} ${intl.formatMessage({id: "form.name"})}`}
          type="default"
          disabled={localUnit?.name.trim() === unit?.name.trim()}
          onClick={() => dispatch(
            editUnit(
              { ...localUnit, unitUUID: localUnit.uuid },
              { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }
            )
          )}
        />
      </div>}
      {((
        localUnit?.externalId.startsWith("NOKE-")
      ) || (
        authUser?.permissions?.includes("unit_set_state") &&
        localUnit?.rentalState === "repo"
      ) || (
        authUser?.permissions?.includes("unit_set_state") &&
        site?.pms === ""
      ) || (
        authUser?.permissions?.includes("support_unit_set_state")
      )) && <div className="flex flex-col gap-4">
        <p className="tw-paragraph-lg">{intl.formatMessage({ id: "pages.units.edit.state" })}</p>
        <SelectInput
          id="Unit-Settings-Device-State-Input"
          dataCy="Unit-Settings-Device-State-Input"
          value={localUnit?.rentalState ?? ""}
          placeholder={intl.formatMessage({ id: "form.state" })}
          onChange={(e) => handleInputChange("rentalState", e.target.value)}
          isDisabled={authUser?.permissions?.includes("unit_set_state") ? false : true}
          options={[
            {
              name: intl.formatMessage({ id: "db.inuse" }),
              value: "inuse"
            },
            {
              name: intl.formatMessage({ id: "db.available" }),
              value: "available"
            },
            ...(canSelectCheckout ? [{
              name: intl.formatMessage({ id: "db.checkout" }),
              value: "checkout"
            }] : []),
            {
              name: intl.formatMessage({ id: "db.repo" }),
              value: "repo" },
            {
              name: intl.formatMessage({ id: "db.overlock" }),
              value: "overlock"
            },
            {
              name: intl.formatMessage({ id: "db.gatelock" }),
              value: "gatelock"
            }
          ]}
        />
        <ButtonContained
          id="Unit-Settings-Device-Save-State"
          dataCy="Unit-Settings-Device-Save-State"
          text={authUser?.permissions?.includes("unit_set_state") ? intl.formatMessage({ id: "comp.button.saveChanges" }) : intl.formatMessage({ id: "comp.button.denied" })}
          type="default"
          disabled={localUnit?.rentalState === normalizeRentalState(unit?.rentalState) || isCheckoutTransitionBlocked}
          onClick={() => {
            if (isCheckoutTransitionBlocked) {
              return;
            }

            dispatch(
              setUnitState(
                localUnit.uuid,
                localUnit.rentalState,
                false,
                { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }
              )
            );
          }}
        />
      </div>}
      {authUser?.permissions?.includes("support_edit_external_ids") && <div className="flex flex-col gap-4">
        <p className="tw-paragraph-lg">{intl.formatMessage({ id: "pages.units.edit.external" })}</p>
        <TextInput
          id="Unit-Settings-Device-External-Id-Input"
          dataCy="Unit-Settings-Device-External-Id-Input"
          placeholder={intl.formatMessage({id: "pages.units.edit.external"})}
          onChange={(e) => handleInputChange("externalId", e.target.value)}
          value={localUnit?.externalId ?? ""}
        />
        <ButtonContained
          id="Unit-Settings-Device-Save-External-Id"
          dataCy="Unit-Settings-Device-Save-External-Id"
          text={intl.formatMessage({ id: "comp.button.saveChanges" })}
          type="default"
          disabled={localUnit?.externalId === unit?.externalId}
          onClick={() => dispatch(
            editUnitExternalId(
              [{ name: localUnit.name, externalId: localUnit.externalId }],
              { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }
            )
          )}
        />
      </div>}
      {authUser?.featureflagAssignments?.includes("motion_notification_shutoff") &&
        authUser?.permissions?.includes("manage_locks") && <div className="flex flex-col gap-4">
        <p className="tw-paragraph-lg">{intl.formatMessage({ id: "form.multipleLocks" })}</p>
        <SelectInput
          id="Unit-Settings-Device-Multiple-Locks"
          dataCy="Unit-Settings-Device-Multiple-Locks"
          value={localUnit?.multipleLockesEnabled ?? false}
          placeholder={intl.formatMessage({ id: "form.multipleLocks" })}
          onChange={(e) => handleInputChange("multipleLockesEnabled", e.target.value === "true")}
          options={[
            {
              name: intl.formatMessage({ id: "comp.form.yes" }),
              value: true
            },
            {
              name: intl.formatMessage({ id: "comp.form.no" }),
              value: false
            },
          ]}
        />
        <ButtonContained
          id="Unit-Settings-Device-Save-Multiple-Locks"
          dataCy="Unit-Settings-Device-Save-Multiple-Locks"
          text={intl.formatMessage({ id: "comp.button.saveChanges" })}
          type="default"
          disabled={localUnit?.multipleLockesEnabled === unit?.multipleLockesEnabled}
          onClick={() => dispatch(
            editUnit(
              { ...localUnit, unitUUID: localUnit.uuid },
              { successMessage: intl.formatMessage({ id: "notifications.success.edit" }) }
            )
          )}
        />
      </div>}
    </div>
  )
};

export default UnitSettingsDeviceModal;